import React, { useState, useMemo } from 'react';
import { useWindowSize } from '../../../hooks';
import { formatNameForDisplay } from '../../../utils/formatUtils';
import { formatRelativeTime, formatDateBR, convertDateBRToISO, formatDate, isValidDateBR } from '../../../utils/dateUtils';
import AppointmentHistory from '../../performance/AppointmentHistory';
import AppointmentCard from '../../ui/AppointmentCard';

/**
 * Componente para exibir os próximos agendamentos
 * 
 * @param {Object} props - Propriedades do componente
 * @param {Array} props.events - Lista de eventos/agendamentos
 * @param {Array} props.resources - Lista de recursos (profissionais)
 * @param {Function} props.onSelectAppointment - Função chamada ao selecionar um agendamento
 * @returns {JSX.Element} - Elemento React
 */
const UpcomingAppointments = ({ 
  events = [], 
  resources = [],
  onSelectAppointment
}) => {
  // Todos os hooks devem ser chamados no início e incondicionalmente
  const { width } = useWindowSize();
  const isMobile = width < 768;
  // Estado para controlar a exibição do modal de histórico
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  // Estados para o filtro de datas
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  // Estados para o filtro de datas em formato brasileiro
  const [startDateBR, setStartDateBR] = useState('');
  const [endDateBR, setEndDateBR] = useState('');
  // Estado para controlar erros de validação
  const [dateErrors, setDateErrors] = useState({ start: '', end: '' });
  // Estado para controlar a exibição dos filtros avançados
  const [showFilters, setShowFilters] = useState(false);
  // Estados para busca e filtragem
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  // Estado para paginação
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Função para formatar a entrada de data no formato brasileiro
  const formatBRDateInput = (value, setter) => {
    // Remove caracteres não numéricos
    let digits = value.replace(/\D/g, '');
    
    // Limita a 8 dígitos
    digits = digits.substring(0, 8);
    
    // Formata como DD/MM/AAAA
    if (digits.length > 4) {
      // Formato: DD/MM/AAAA
      setter(`${digits.substring(0, 2)}/${digits.substring(2, 4)}/${digits.substring(4)}`);
    } else if (digits.length > 2) {
      // Formato: DD/MM
      setter(`${digits.substring(0, 2)}/${digits.substring(2)}`);
    } else {
      // Apenas dígitos
      setter(digits);
    }
  };

  // Função para validar e converter data brasileira para ISO
  const handleBRDateBlur = (value, isoSetter, errorType) => {
    // Se estiver vazio, limpar tudo
    if (!value.trim()) {
      isoSetter('');
      setDateErrors(prev => ({ ...prev, [errorType]: '' }));
      return;
    }
    
    // Verificar se é uma data válida
    if (isValidDateBR(value)) {
      const isoDate = convertDateBRToISO(value);
      isoSetter(isoDate);
      setDateErrors(prev => ({ ...prev, [errorType]: '' }));
    } else {
      setDateErrors(prev => ({ ...prev, [errorType]: 'Data inválida' }));
    }
  };

  // Filtrar apenas eventos futuros em ordem cronológica
  // Removendo os eventos cancelados da lista
  const now = new Date();
  const upcomingEvents = events
    .filter(event => {
      // Verificar se o evento é futuro
      const eventDate = new Date(event.start);
      
      // Verificar se o agendamento foi cancelado (verificando tanto event.cancelado quanto event.appointment?.cancelado)
      const isCanceled = event.cancelado || (event.appointment && event.appointment.cancelado);
      
      // Incluir apenas eventos futuros e não cancelados
      return eventDate > now && !isCanceled;
    })
    .sort((a, b) => new Date(a.start) - new Date(b.start))
    .slice(0, isMobile ? 3 : 5); // Limitar o número de eventos mostrados em dispositivos móveis

  /**
   * Formata os eventos para o formato esperado pelo componente AppointmentHistory
   * Garante compatibilidade entre a estrutura de eventos do calendário e o formato esperado pelo AppointmentCard
   * 
   * @param {Array} eventsList - Lista de eventos do calendário
   * @returns {Array} - Lista formatada de agendamentos
   */
  const formatEventsForHistory = (eventsList) => {
    return eventsList.map(event => {
      const start = new Date(event.start);
      
      // Verifica status e aplica lógica de decisão
      let status = 'completed'; // Valor padrão
      if (event.appointment?.cancelado) {
        status = 'canceled';
      } else if (event.appointment?.no_show) {
        status = 'no-show';
      } else if (event.appointment?.confirmado) {
        status = 'completed';
      }
      
      // Verifica se o pagamento está confirmado
      const isPaid = event.appointment?.pago === true || 
                    event.appointment?.pagamentoConfirmado === true ||
                    Boolean(event.appointment?.metodoPagamento && 
                            event.appointment?.metodoPagamento !== 'Não informado');
      
      // Obtém o método de pagamento
      const paymentMethod = event.appointment?.metodoPagamento || 'Não informado';
      
      return {
        id: event.id,
        clientName: event.clientName || 'Cliente',
        serviceName: event.title || 'Serviço',
        date: start,
        time: start.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
        status,
        professionalName: resources.find(r => r.id === event.resourceId)?.title || 'Profissional',
        value: event.appointment?.valorTotal || 0,
        duration: event.appointment?.duracaoTotal || 30,
        isPaid: isPaid,
        paymentMethod: paymentMethod,
        // Dados de contato do cliente
        clientEmail: event.appointment?.email || '',
        clientPhone: event.appointment?.whatsapp || '',
        // Campo original para compatibilidade
        appointment: event.appointment
      };
    });
  };

  // Formatar os eventos para o histórico completo
  const formattedAppointments = formatEventsForHistory(events);

  // Aplicar todos os filtros aos agendamentos formatados
  const filteredAppointments = useMemo(() => {
    return formattedAppointments.filter(appointment => {
      const appointmentDate = new Date(appointment.date);
      
      // Filtro de datas
      const dateFilter = () => {
        // Se não houver filtros de data, retorna true
        if (!startDate && !endDate) return true;
        
        // Converter as datas do formato de entrada (ISO) para objeto Date
        const startDateObj = startDate ? new Date(startDate + "T00:00:00") : null;
        const endDateObj = endDate ? new Date(endDate + "T23:59:59") : null;
        
        // Verificar se data está dentro do intervalo
        const startOk = !startDateObj || appointmentDate >= startDateObj;
        const endOk = !endDateObj || appointmentDate <= endDateObj;
        
        return startOk && endOk;
      };
      
      // Filtro de status
      const statusFilterFn = () => {
        if (statusFilter === 'all') return true;
        return appointment.status === statusFilter;
      };
      
      // Filtro de busca textual
      const searchFilter = () => {
        if (!searchTerm) return true;
        
        const term = searchTerm.toLowerCase();
        
        // Buscar em vários campos
        return (
          appointment.clientName.toLowerCase().includes(term) ||
          appointment.clientPhone.toLowerCase().includes(term.replace(/\D/g, '')) ||
          appointment.clientEmail.toLowerCase().includes(term) ||
          appointment.serviceName.toLowerCase().includes(term)
        );
      };
      
      // Aplicar todos os filtros com operador AND
      return dateFilter() && statusFilterFn() && searchFilter();
    });
  }, [formattedAppointments, startDate, endDate, statusFilter, searchTerm]);

  // Calcular número total de páginas
  const totalPages = Math.ceil(filteredAppointments.length / itemsPerPage);
  
  // Obter os agendamentos da página atual
  const currentItems = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAppointments.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAppointments, currentPage]);

  // Reset dos filtros
  const resetAllFilters = () => {
    setStartDate('');
    setEndDate('');
    setStartDateBR('');
    setEndDateBR('');
    setDateErrors({ start: '', end: '' });
    setSearchTerm('');
    setStatusFilter('all');
    setCurrentPage(1);
  };

  // Função para renderizar os controles de paginação
  const renderPagination = () => {
    if (filteredAppointments.length <= itemsPerPage) return null;
    
    return (
      <div className="mt-6 flex justify-center">
        <nav className="flex items-center space-x-2">
          <button 
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className={`px-3 py-1 rounded-md ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50 border'}`}
          >
            Anterior
          </button>
          
          <span className="px-3 py-1 text-sm text-gray-700">
            Página {currentPage} de {totalPages}
          </span>
          
          <button 
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className={`px-3 py-1 rounded-md ${currentPage === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50 border'}`}
          >
            Próxima
          </button>
        </nav>
      </div>
    );
  };

  /**
   * Encontra e formata o nome do profissional a partir do ID
   * 
   * @param {string} resourceId - ID do recurso/profissional
   * @returns {string} - Nome do profissional formatado
   */
  const getResourceTitle = (resourceId) => {
    const resource = resources.find(r => r.id === resourceId);
    if (!resource) return 'Qualquer profissional';
    return formatNameForDisplay(resource.title);
  };

  // Formatação para exibir o horário do evento
  const formatEventTime = (event) => {
    const start = new Date(event.start);
    return start.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Formatar a data do evento
  const formatEventDate = (event) => {
    const start = new Date(event.start);
    return start.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
    });
  };

  /**
   * Trunca nomes de clientes muito longos para melhor apresentação
   * 
   * @param {string} name - Nome do cliente
   * @returns {string} - Nome truncado ou original
   */
  const formatClientName = (name) => {
    if (!name) return 'Cliente';
    return name.length > 16 ? `${name.slice(0, 15)}…` : name;
  };

  if (upcomingEvents.length === 0) {
    return (
      <div className="upcoming-appointments bg-white rounded shadow">
        <div className="p-3 border-b">
          <h3 className="text-lg font-medium text-gray-800">Próximos Agendamentos</h3>
        </div>
        <div className="p-4 text-center text-gray-500 italic">
          Não há agendamentos próximos.
        </div>
      </div>
    );
  }

  return (
    <div className="upcoming-appointments bg-white rounded shadow">
      <div className="p-3 border-b">
        <h3 className="text-lg font-medium text-gray-800">Próximos Agendamentos</h3>
      </div>
      <ul className="divide-y divide-gray-100">
        {upcomingEvents.map((event) => (
          <li 
            key={event.id} 
            className="p-3 hover:bg-gray-50 cursor-pointer transition duration-150 ease-in-out"
            onClick={() => onSelectAppointment(event)}
          >
            <div className="flex justify-between items-start">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {event.title || 'Sem título'}
                </p>
                <div className="mt-1 flex flex-wrap gap-y-1">
                  <div className={`flex items-center mr-3 ${isMobile ? 'text-xs' : 'text-sm'} text-gray-500`}>
                    <svg className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                    </svg>
                    <span className="truncate max-w-[100px]" title={event.clientName || 'Cliente'}>
                      {formatNameForDisplay(event.clientName || 'Cliente', 16)}
                    </span>
                  </div>
                  {!isMobile && (
                    <div className="flex items-center mr-3 text-sm text-gray-500">
                      <svg className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                      </svg>
                      <span>{formatEventDate(event)}</span>
                    </div>
                  )}
                  <div className="flex items-center text-sm text-gray-500">
                    <svg className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                    </svg>
                    <span>{formatEventTime(event)}</span>
                  </div>
                </div>
              </div>
              <div className="ml-2 flex-shrink-0 flex flex-col items-end">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {formatRelativeTime(event.start)}
                </span>
                {!isMobile && (
                  <span 
                    className="mt-1 text-xs text-gray-500 truncate max-w-[100px]"
                    title={resources.find(r => r.id === event.resourceId)?.title || 'Qualquer profissional'}
                  >
                    {getResourceTitle(event.resourceId)}
                  </span>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
      {upcomingEvents.length > 0 && (
        <div className="p-3 border-t text-center">
          <button 
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            onClick={() => setHistoryModalOpen(true)}
          >
            Ver todos os agendamentos
          </button>
        </div>
      )}

      {/* Modal de histórico de agendamentos - Experiência moderna */}
      {historyModalOpen && (
        <div className={`appointment-history-modal fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center ${isMobile ? 'items-start' : 'items-center'} p-0 sm:p-4 animate-fadeIn`}>
          <div 
            className={`appointment-history-container bg-white ${isMobile ? 'w-full h-full rounded-none' : 'rounded-lg shadow-xl w-4/5 max-w-4xl max-h-[85vh]'} 
            overflow-hidden flex flex-col animate-slideUp`}
          >
            {/* Cabeçalho */}
            <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white z-10">
              <h3 className="text-xl font-semibold text-gray-800">Histórico de Agendamentos</h3>
              <button 
                onClick={() => setHistoryModalOpen(false)}
                className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100 transition-colors"
                aria-label="Fechar modal"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Barra de busca principal */}
            <div className="appointment-search-bar p-4 border-b">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                {/* Campo de busca com ícone */}
                <div className="appointment-search-input-container relative flex-grow">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    aria-label="Buscar agendamentos"
                    placeholder="Buscar por nome, telefone, serviço ou email..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1); // Resetar para primeira página ao buscar
                    }}
                    style={{ width: '100%', boxSizing: 'border-box' }}
                    className="appointment-search-input pl-10 w-full h-10 px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearchTerm('');
                        setCurrentPage(1);
                      }}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                      aria-label="Limpar pesquisa"
                    >
                      <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </button>
                  )}
                </div>
                
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-2 sm:flex-shrink-0">
                  {/* Seleção de status */}
                  <div className="w-full sm:w-auto flex-shrink-0">
                    <select
                      value={statusFilter}
                      onChange={(e) => {
                        setStatusFilter(e.target.value);
                        setCurrentPage(1); // Resetar para primeira página ao filtrar
                      }}
                      className="block w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
                    >
                      <option value="all">Todos os status</option>
                      <option value="completed">Concluídos</option>
                      <option value="canceled">Cancelados</option>
                      <option value="no-show">Não compareceu</option>
                    </select>
                  </div>
                  
                  {/* Botão de filtros avançados */}
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`flex items-center justify-center gap-2 px-3 py-2 border rounded-lg shadow-sm flex-shrink-0 ${showFilters ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-gray-300 text-gray-700'} text-sm`}
                  >
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
                    </svg>
                    <span className="hidden sm:inline">Filtros</span>
                  </button>
                </div>
              </div>
            </div>
            
            {/* Filtros avançados - ocultos por padrão */}
            {showFilters && (
              <div className="p-4 border-b bg-gray-50 animate-slideDown">
                <div className={`${isMobile ? 'flex flex-col gap-3' : 'flex flex-row gap-3 items-end'}`}>
                  <div className="w-full sm:w-auto">
                    <label htmlFor="startDateBR" className="block text-sm font-medium text-gray-700 mb-1">De</label>
                    <input 
                      type="text" 
                      id="startDateBR"
                      placeholder="DD/MM/AAAA"
                      value={startDateBR}
                      onChange={(e) => formatBRDateInput(e.target.value, setStartDateBR)}
                      onBlur={() => handleBRDateBlur(startDateBR, setStartDate, 'start')}
                      className={`w-full px-3 py-2 border ${dateErrors.start ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                    />
                    {dateErrors.start && (
                      <p className="mt-1 text-xs text-red-500">{dateErrors.start}</p>
                    )}
                  </div>
                  <div className="w-full sm:w-auto">
                    <label htmlFor="endDateBR" className="block text-sm font-medium text-gray-700 mb-1">Até</label>
                    <input 
                      type="text" 
                      id="endDateBR"
                      placeholder="DD/MM/AAAA"
                      value={endDateBR}
                      onChange={(e) => formatBRDateInput(e.target.value, setEndDateBR)}
                      onBlur={() => handleBRDateBlur(endDateBR, setEndDate, 'end')}
                      className={`w-full px-3 py-2 border ${dateErrors.end ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                    />
                    {dateErrors.end && (
                      <p className="mt-1 text-xs text-red-500">{dateErrors.end}</p>
                    )}
                  </div>
                  <div className={`${isMobile ? 'w-full' : 'flex space-x-2 self-end'}`}>
                    <button 
                      onClick={resetAllFilters}
                      className={`${isMobile ? 'w-full mt-2' : ''} px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm bg-white hover:bg-gray-50`}
                    >
                      Limpar todos os filtros
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Contador de resultados */}
            <div className="px-4 pt-4 pb-2">
              <p className="text-sm text-gray-600">
                {filteredAppointments.length} 
                {filteredAppointments.length === 1 ? ' agendamento encontrado' : ' agendamentos encontrados'}
                {searchTerm && <span> para "<strong>{searchTerm}</strong>"</span>}
              </p>
            </div>
            
            <div className="flex-1 overflow-auto p-4">
              {currentItems.length > 0 ? (
                <div className="space-y-3">
                  {currentItems.map(appointment => (
                    <AppointmentCard 
                      key={appointment.id}
                      appointment={appointment}
                      isMobile={isMobile}
                      showDetails={true}
                    />
                  ))}
                  
                  {/* Paginação */}
                  {renderPagination()}
                </div>
              ) : (
                <div className="text-center py-10">
                  <svg className="mx-auto h-12 w-12 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum agendamento encontrado</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Tente ajustar seus filtros ou fazer uma busca diferente.
                  </p>
                  <div className="mt-6">
                    <button
                      onClick={resetAllFilters}
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Limpar filtros
                    </button>
                  </div>
                </div>
              )}
            </div>
            
            <div className="p-4 border-t flex justify-end sticky bottom-0 bg-white">
              <button 
                onClick={() => setHistoryModalOpen(false)}
                className={`px-4 py-2 ${isMobile ? 'w-full text-center font-medium' : ''} bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg transition-colors`}
              >
                {isMobile ? 'Voltar para Agenda' : 'Fechar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UpcomingAppointments; 